import type { ProgrammaticLandingPageConfig } from '../../types'
import { KWP_SIZE_FACTS } from '../kwpSizeFacts'

const FACTS = KWP_SIZE_FACTS[20]

export const KWP_20_INSTALACJA_FOTOWOLTAICZNA: ProgrammaticLandingPageConfig = {
  slug: 'instalacja-fotowoltaiczna-20-kwp-cena',
  lang: 'pl',
  topicId: 'kwp-kosten-20',
  title: 'Instalacja fotowoltaiczna 20 kWp: cena i wartości orientacyjne',
  metaDescription:
    'Ile kosztuje instalacja fotowoltaiczna 20 kWp? Powierzchnia dachu, liczba paneli, ' +
    'rozbicie kosztów i bezpłatne porównanie cen dla domów dwurodzinnych z wysokim zużyciem.',
  keywords: [
    'instalacja fotowoltaiczna 20 kWp cena',
    'panele słoneczne 20 kWp koszt',
    'ile kosztuje instalacja 20 kWp',
    '20 kWp instalacja fotowoltaiczna dom dwurodzinny',
  ],
  eyebrow: 'Wielkość instalacji: 20 kWp',
  h1: 'Instalacja fotowoltaiczna 20 kWp: cena i wartości orientacyjne',
  intro:
    'Instalacja 20 kWp odpowiednia jest dla domów dwurodzinnych lub budynków z bardzo wysokim ' +
    'łącznym zużyciem energii. Znajdziesz tu wartości orientacyjne dotyczące powierzchni dachu i ' +
    'liczby paneli, rozbicie kosztów, a tuż poniżej kalkulator z już wypełnioną średnią ceną dla ' +
    '20 kWp, obliczoną na podstawie prawdziwych ofert firm rzemieślniczych.',
  calculatorPrefill: { kind: 'kwp', kwp: 20 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: 'Dla kogo odpowiednia jest instalacja 20 kWp?' },
    {
      type: 'paragraph',
      text:
        'Wartość orientacyjna: 20 kWp zwykle pasuje do domu dwurodzinnego z dwoma niezależnymi ' +
        'gospodarstwami lub domu jednorodzinnego z bardzo wysokim łącznym zużyciem (pompa ciepła, ' +
        'samochód elektryczny, ewentualnie basen), całkowite roczne zużycie często w przedziale ' +
        '12 000-16 000 kWh.',
    },
    { type: 'heading', text: 'Dach i panele: czego potrzebuje instalacja 20 kWp' },
    {
      type: 'paragraph',
      text:
        `Wartość orientacyjna: ok. ${FACTS.roofAreaM2} m² powierzchni dachu i ok. ` +
        `${FACTS.moduleCount} paneli przy typowych modułach ~440 Wp. Roczna produkcja przy ` +
        `orientacji południowej bez zacienienia wynosi ok. ${FACTS.annualYieldKwh.toLocaleString('pl-PL')} kWh. ` +
        'Przy tej wielkości często nie wystarcza już jedna połać dachu - dokładne obliczenie z ' +
        'uwzględnieniem kilku orientacji daje bezpłatny kalkulator doboru instalacji.',
    },
    { type: 'heading', text: 'Składniki kosztów instalacji fotowoltaicznej' },
    {
      type: 'list',
      items: [
        'Panele: największa pojedyncza pozycja kosztowa, zwykle około jednej trzeciej całości',
        'Falownik: przy 20 kWp zwykle kilka stringów, czasem kilka falowników',
        'Montaż/robocizna: mocowanie na dachu, okablowanie, więcej pracy przy kilku połaciach',
        'Rusztowanie: przy większej powierzchni dachu odpowiednio większe',
        'Instalacja elektryczna: dostosowanie rozdzielnicy, ewentualnie osobne liczniki przy dwóch gospodarstwach',
        'Zgłoszenie: rejestracja u operatora sieci i w odpowiednich rejestrach',
      ],
    },
    {
      type: 'paragraph',
      text:
        'W domu dwurodzinnym z dwoma niezależnymi gospodarstwami firma rzemieślnicza wyjaśnia w ' +
        'ofercie dodatkowo, jak technicznie i rozliczeniowo podzielić produkcję i zużycie energii ' +
        'między obie strony.',
    },
    { type: 'heading', text: 'Amortyzacja i autokonsumpcja' },
    {
      type: 'paragraph',
      text:
        '20 kWp wciąż mieści się poniżej granicy 30 kWp, do której w Niemczech przychody z ' +
        'instalacji fotowoltaicznych są całkowicie zwolnione z podatku dochodowego, a na zakup i ' +
        'instalację obowiązuje 0% VAT. Dokładne obliczenie autokonsumpcji i amortyzacji dla Twojego ' +
        'dachu daje bezpłatny kalkulator doboru instalacji.',
    },
  ],
  faq: [
    {
      q: 'Czy przy instalacji 20 kWp zmienia się coś podatkowo lub prawnie?',
      a: 'Nie - 20 kWp wciąż mieści się poniżej granicy 30 kWp dla pełnego zwolnienia z podatku dochodowego i 0% VAT na zakup i instalację w Niemczech.',
    },
    {
      q: 'Czy instalacja 20 kWp jest odpowiednia dla domu dwurodzinnego?',
      a: 'Tak, to jedno z typowych zastosowań tej wielkości. Firma rzemieślnicza wyjaśnia w ofercie, jak podzielić produkcję i zużycie energii między oba gospodarstwa.',
    },
    {
      q: 'Czy jedna połać dachu wystarczy na 20 kWp?',
      a: `Wartość orientacyjna: potrzeba ok. ${FACTS.roofAreaM2} m² - przy mniejszych lub nietypowo ukształtowanych dachach firmy rzemieślnicze często rozkładają panele na kilka połaci o różnej orientacji.`,
    },
  ],
  primaryCta: { label: 'Poproś teraz o bezpłatną ofertę', action: 'customer' },
  secondaryCta: {
    label: 'Koszt instalacji fotowoltaicznej: pełny przewodnik',
    action: 'link',
    href: '/koszt-instalacji-fotowoltaicznej',
  },
  relatedLinks: [
    { label: 'Instalacja fotowoltaiczna 15 kWp: cena i wartości orientacyjne', href: '/instalacja-fotowoltaiczna-15-kwp-cena' },
    { label: 'Instalacja fotowoltaiczna 25 kWp: cena i wartości orientacyjne', href: '/instalacja-fotowoltaiczna-25-kwp-cena' },
    { label: 'Instalacja fotowoltaiczna 12 kWp: cena i wartości orientacyjne', href: '/instalacja-fotowoltaiczna-12-kwp-cena' },
    { label: 'Magazyn energii 20 kWh: cena i wartości orientacyjne', href: '/magazyn-energii-20-kwh-cena' },
    { label: 'Jak działa porównanie ofert', href: '/porownaj-oferty-fotowoltaiczne' },
  ],
}
